import { createTheme } from '@mui/material/styles';
import { CSSProperties } from 'react';
import InterTtf from './fonts/inter/Inter-VariableFont_slnt,wght.ttf';
import SatoshiVariableTtf from './fonts/satoshi/Satoshi-Variable.ttf';
import SatoshiVariableWoff from './fonts/satoshi/Satoshi-Variable.woff';
import SatoshiVariableWoff2 from './fonts/satoshi/Satoshi-Variable.woff2';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: {
      light: string;
    };
    brandOrange: {
      light: string;
      main: string;
      dark: string;
    };
  }
  interface PaletteOptions {
    neutral?: {
      light?: string;
    };
    brandOrange?: {
      light?: string;
      main?: string;
      dark?: string;
    };
  }
  interface Components {
    helperText?: CSSProperties;
    returnToText?: CSSProperties;
    contentTitleText?: CSSProperties;
  }
}

const Inter = {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontDisplay: 'swap',
    fontWeight: 300,
    src: `
        local('Inter'),
        local('inter'),
        url(${InterTtf}) format('truetype')
    `,
};

const SatoshiVariable = {
    fontFamily: 'Satoshi-Variable',
    fontStyle: 'normal',
    fontDisplay: 'swap',
    fontWeight: 700,
    src: `
        local('Satoshi-Variable'),
        url(${SatoshiVariableWoff2}) format('woff2'),
        url(${SatoshiVariableWoff}) format('woff'),
        url(${SatoshiVariableTtf}) format('truetype')
    `,
};

const globalThemes = createTheme({
    palette: {
        primary: {
            light: '#757DE8',
            main: '#3F51B5',
            dark: '#002984',
        },
        secondary: {
            light: '#757DE8',
            main: '#3F51B5',
            dark: '#002984',
        },
        text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)',
            disabled: 'rgba(0, 0, 0, 0.38)',
        },
        error: {
            light: '#ef5350',
            main: '#d32f2f',
            dark: '#c62828'
        },
        warning: {
            light: '#ff9800',
            main: '#ED6C02',
            dark: '#e65100'
        },
        success: {
            light: "rgba(76, 175, 80, 0.08)",
            main: '#4CAF50',
        },
        neutral: {
            light: "rgba(63, 81, 181, 0.08)",
        },
        // SecurSpace brand orange (design-system token). The Notifications matrix
        // accent reads from a single constant (see ChannelControl) so it can be
        // swapped to brandOrange.main in one place.
        brandOrange: {
            light: "rgba(247, 144, 25, 0.10)",
            main: '#F79019',
            dark: '#D77119',
        },
    },
    typography: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 14,
        h1: {
            fontFamily: 'Satoshi-Variable',
            fontWeight: 700,
            fontSize: '4.57rem',
            lineHeight: '6.14rem',
        },
        h3: {
            fontFamily: 'Satoshi-Variable',
            fontWeight: 700,
            fontSize: '2.29rem',
            lineHeight: '3.07rem',
        },
        h5: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '1.71rem',
            lineHeight: '133.4%',
        },
        h6: {
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '1.14rem',
            lineHeight: '160%',
            letterSpacing: '0.01rem',
        },
        subtitle1: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '1.14rem',
            lineHeight: '175%',
            letterSpacing: '0.01rem',
        },
        subtitle2: {
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: '157%',
            letterSpacing: '0.007rem',
        },
        body1: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '1.14rem',
            lineHeight: '150%',
            letterSpacing: '0.01rem',
        },
        body2: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: '143%',
            letterSpacing: '0.01rem',
        },
        caption: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '0.86rem',
            lineHeight: '166%',
            letterSpacing: '0.03rem',
        },
        overline: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '0.86rem',
            lineHeight: '266%',
            letterSpacing: '0.07rem',
            textTransform: 'uppercase',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '@font-face': [Inter, SatoshiVariable],
            },
        },
        MuiInputBase: {
            styleOverrides: {
                // iOS Safari auto-zooms the viewport on focus when an input's computed font-size is under 16px.
                // Keyed on pointer rather than width so it also covers phones in landscape, which exceed `sm`.
                // Repeated `&` raises specificity above component-level `& .MuiInputBase-input` / `& input`
                // rules, which would otherwise outrank a plain slot override and reintroduce the zoom.
                input: {
                    '@media (pointer: coarse)': {
                        '&&&': {
                            fontSize: '16px',
                        },
                    },
                },
            },
        },
        helperText: {
            fontFamily: 'Inter',
            fontSize: '0.86rem',
            fontWeight: 400,
            lineHeight: '167%',
            letterSpacing: '0.03rem',
        },
        returnToText: {
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '0.71rem',
            lineHeight: '100%',
            textTransform: 'uppercase',
        },
        contentTitleText: {
            fontFamily: 'Inter',
            fontSize: '1.43rem',
            fontWeight: 600,
            lineHeight: '160%',
            letterSpacing: '0.01rem',
        }
    },
})


export default globalThemes;
